import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AlertCircle,
  Columns2,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDown,
  Plus as PlusIcon,
  RefreshCw,
  Search,
  Tags
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const UI_COLORS = {
  indigo: '#4f46e5',
  sky: '#0ea5e9',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444'
};

const translateAmbito = (value) => {
  const map = { cliente: 'Cliente', studio: 'Studio', entrambi: 'Entrambi' };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const AmbitoChip = ({ ambito }) => {
  const key = String(ambito || '').toLowerCase();
  const style = key === 'cliente' ? { color: UI_COLORS.green } : key === 'studio' ? { color: UI_COLORS.indigo } : { color: UI_COLORS.amber };
  return (
    <Chip
      label={translateAmbito(key) || '-'}
      size="small"
      variant="outlined"
      sx={{ textTransform: 'capitalize', fontWeight: 700, color: style.color, borderColor: style.color }}
    />
  );
};

const TotaleRecessiChip = ({ value }) => {
  const n = Number(value || 0);
  return (
    <Chip
      label={Number.isFinite(n) ? String(n) : '0'}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 900,
        color: UI_COLORS.amber,
        borderColor: `${UI_COLORS.amber}80`,
        minWidth: 56,
        justifyContent: 'center'
      }}
    />
  );
};

const PercentDonut = ({ value }) => {
  const raw = Number(value || 0);
  const pct = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : 0;
  const size = 38;
  const thickness = 5;
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{ color: 'rgba(15, 23, 42, 0.10)' }}
      />
      <CircularProgress
        variant="determinate"
        value={pct}
        size={size}
        thickness={thickness}
        sx={{ position: 'absolute', left: 0, color: UI_COLORS.amber }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          {pct}%
        </Typography>
      </Box>
    </Box>
  );
};

const formatBool = (value) => (value ? 'Attivo' : 'Disattivo');

const parseBlobError = async (data) => {
  try {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed?.message || text;
      } catch (_) {
        return text;
      }
    }
    if (typeof data === 'object') {
      return data?.message || null;
    }
    return null;
  } catch (_) {
    return null;
  }
};

export default function RecessiMotivazioni() {
  const columnDefs = useMemo(
    () => [
      { key: 'ambito', label: 'Ambito', sortable: true, align: 'left' },
      { key: 'codice', label: 'Codice', sortable: true, align: 'left' },
      { key: 'descrizione', label: 'Descrizione', sortable: true, align: 'left' },
      { key: 'totale_recessi', label: 'Totale recessi', sortable: true, align: 'center' },
      { key: 'perc_totale', label: '% sul totale', sortable: true, align: 'center' },
      { key: 'ordine', label: 'Ordine', sortable: true, align: 'right' },
      { key: 'attivo', label: 'Stato', sortable: true, align: 'left' }
    ],
    []
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('ordine');
  const [filterText, setFilterText] = useState('');
  const [filterAmbito, setFilterAmbito] = useState('');
  const [filterAttivo, setFilterAttivo] = useState('');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(['ambito', 'codice', 'descrizione', 'totale_recessi', 'perc_totale', 'ordine', 'attivo']);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [formData, setFormData] = useState({
    ambito: 'entrambi',
    codice: '',
    descrizione: '',
    ordine: 0,
    attivo: true
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/recessi/motivazioni');
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching recessi motivazioni:', err);
      toast.error('Errore nel caricamento delle motivazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (reporting) return;
    setReporting(true);
    try {
      const res = await api.get('/recessi/motivazioni/report/pdf', {
        responseType: 'blob',
        params: {
          search: filterText || '',
          ambito: filterAmbito || '',
          onlyActive: filterAttivo === '1' ? 1 : 0
        }
      });

      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/pdf'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const now = new Date();
      const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(
        2,
        '0'
      )}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      link.href = url;
      link.download = `recessi_motivazioni_${ts}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const message = await parseBlobError(error?.response?.data);
      toast.error(message || 'Errore durante la generazione del report');
    } finally {
      setReporting(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openCreate = () => {
    setCurrentRow(null);
    setFormData({ ambito: 'entrambi', codice: '', descrizione: '', ordine: 0, attivo: true });
    setOpenDialog(true);
  };

  const openEdit = (row) => {
    setCurrentRow(row);
    setFormData({
      ambito: row?.ambito || 'entrambi',
      codice: row?.codice || '',
      descrizione: row?.descrizione || '',
      ordine: Number(row?.ordine || 0),
      attivo: Boolean(row?.attivo)
    });
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const compareText = (a, b) => {
    const sa = String(a || '').toLowerCase();
    const sb = String(b || '').toLowerCase();
    if (sa < sb) return -1;
    if (sa > sb) return 1;
    return 0;
  };

  const compareNumber = (a, b) => {
    const na = Number(a || 0);
    const nb = Number(b || 0);
    if (na < nb) return -1;
    if (na > nb) return 1;
    return 0;
  };

  const filteredBaseRows = rows.filter((row) => {
    const term = String(filterText || '').trim().toLowerCase();
    if (filterAmbito && String(row?.ambito || '') !== filterAmbito) return false;
    if (filterAttivo) {
      const wanted = filterAttivo === '1';
      if (Boolean(row?.attivo) !== wanted) return false;
    }
    if (!term) return true;
    const hay = `${row?.codice || ''} ${row?.descrizione || ''}`.toLowerCase();
    return hay.includes(term);
  });

  const totaleRecessiFiltered = filteredBaseRows.reduce((acc, row) => {
    const n = Number(row?.totale_recessi || 0);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);

  const filteredRows = filteredBaseRows
    .map((row) => {
      const n = Number(row?.totale_recessi || 0);
      const safeN = Number.isFinite(n) ? n : 0;
      const pct = totaleRecessiFiltered > 0 ? Math.round((safeN / totaleRecessiFiltered) * 100) : 0;
      return { ...row, totale_recessi: safeN, perc_totale: pct };
    })
    .sort((a, b) => {
      const dir = order === 'asc' ? 1 : -1;
      const key = String(orderBy || '');
      if (key === 'ordine') return compareNumber(a?.ordine, b?.ordine) * dir;
      if (key === 'attivo') return compareNumber(Boolean(a?.attivo) ? 1 : 0, Boolean(b?.attivo) ? 1 : 0) * dir;
      if (key === 'totale_recessi') return compareNumber(a?.totale_recessi, b?.totale_recessi) * dir;
      if (key === 'perc_totale') return compareNumber(a?.perc_totale, b?.perc_totale) * dir;
      return compareText(a?.[key], b?.[key]) * dir;
    });

  const displayedColumns = useMemo(() => columnDefs.filter((c) => visibleColumns.includes(c.key)), [columnDefs, visibleColumns]);

  const handleToggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) return prev.filter((c) => c !== key);
      return [...prev, key];
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ambito: String(formData.ambito || 'entrambi'),
        codice: String(formData.codice || '').trim(),
        descrizione: String(formData.descrizione || '').trim(),
        ordine: Number(formData.ordine || 0),
        attivo: Boolean(formData.attivo)
      };
      if (!payload.codice) {
        toast.error('Codice obbligatorio');
        return;
      }
      if (!payload.descrizione) {
        toast.error('Descrizione obbligatoria');
        return;
      }

      if (currentRow?.id_motivazione) {
        await api.put(`/recessi/motivazioni/${currentRow.id_motivazione}`, payload);
        toast.success('Motivazione aggiornata');
      } else {
        await api.post('/recessi/motivazioni', payload);
        toast.success('Motivazione creata');
      }
      closeDialog();
      fetchRows();
    } catch (e) {
      const msg = e?.response?.data?.message || 'Errore salvataggio';
      toast.error(msg);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id_motivazione) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/recessi/motivazioni/${deleteTarget.id_motivazione}`);
      toast.success('Motivazione eliminata');
      closeDeleteDialog();
      fetchRows();
    } catch (e) {
      const msg = e?.response?.data?.message || 'Errore durante eliminazione';
      toast.error(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: `${UI_COLORS.amber}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Tags size={20} color={UI_COLORS.amber} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Motivazioni
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Anagrafica motivazioni per recessi
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outline"
            onClick={handleDownloadReport}
            title="Report PDF"
            disabled={reporting}
          >
            <div className="flex items-center gap-2">
              <FileDown className="h-4 w-4" />
              <span>{reporting ? 'Report...' : 'Report'}</span>
            </div>
          </Button>
          <Button variant="outline" onClick={fetchRows} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openCreate}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuova motivazione
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <CardContent>
          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 }
            }}
          >
            <Grid item sx={{ minWidth: 320, flex: '0 0 auto' }}>
              <TextField
                placeholder="Cerca per codice o descrizione..."
                variant="outlined"
                size="small"
                fullWidth
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item sx={{ minWidth: 190, flex: '0 0 auto' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tutti gli ambiti</InputLabel>
                <Select
                  label="Tutti gli ambiti"
                  value={filterAmbito}
                  onChange={(e) => {
                    setFilterAmbito(String(e.target.value || ''));
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti gli ambiti</MenuItem>
                  <MenuItem value="cliente">Cliente</MenuItem>
                  <MenuItem value="studio">Studio</MenuItem>
                  <MenuItem value="entrambi">Entrambi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sx={{ minWidth: 200, flex: '0 0 auto' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato</InputLabel>
                <Select
                  label="Stato"
                  value={filterAttivo}
                  onChange={(e) => {
                    setFilterAttivo(String(e.target.value || ''));
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="1">Attivi</MenuItem>
                  <MenuItem value="0">Disattivi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item sx={{ flex: '1 1 auto' }} />
            <Grid item sx={{ minWidth: 160, flex: '0 0 auto', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outline"
                onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                title="Colonne"
                className="flex items-center gap-2"
              >
                <Columns2 className="h-4 w-4" />
                Colonne
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
              >
                {columnDefs.map((c) => (
                  <MenuItem key={c.key} onClick={() => handleToggleColumn(c.key)}>
                    <Checkbox checked={visibleColumns.includes(c.key)} size="small" />
                    {c.label}
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {displayedColumns.map((column) => (
                    <TableCell key={column.key} align={column.align} sx={{ whiteSpace: 'nowrap' }}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.key}
                          direction={orderBy === column.key ? order : 'asc'}
                          onClick={() => handleRequestSort(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nessun dato
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow key={row.id_motivazione} hover>
                        {displayedColumns.map((col) => {
                          if (col.key === 'ambito') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <AmbitoChip ambito={row.ambito} />
                              </TableCell>
                            );
                          }
                          if (col.key === 'totale_recessi') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <TotaleRecessiChip value={row.totale_recessi} />
                                </Box>
                              </TableCell>
                            );
                          }
                          if (col.key === 'perc_totale') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <PercentDonut value={row.perc_totale} />
                                </Box>
                              </TableCell>
                            );
                          }
                          if (col.key === 'attivo') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <Chip
                                  label={formatBool(Boolean(row.attivo))}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 700,
                                    color: row.attivo ? UI_COLORS.green : 'rgba(15, 23, 42, 0.55)',
                                    borderColor: row.attivo ? UI_COLORS.green : 'rgba(15, 23, 42, 0.25)'
                                  }}
                                />
                              </TableCell>
                            );
                          }
                          if (col.key === 'ordine') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap', fontWeight: 800 }}>
                                {Number(row.ordine || 0)}
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                              {row[col.key] ?? ''}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton size="small" onClick={() => openEdit(row)} title="Modifica">
                            <EditIcon className="h-4 w-4" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openDeleteDialog(row)} title="Elimina">
                            <DeleteIcon className="h-4 w-4 text-red-500" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{currentRow?.id_motivazione ? 'Modifica motivazione' : 'Nuova motivazione'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Ambito</InputLabel>
                <Select
                  label="Ambito"
                  value={formData.ambito}
                  onChange={(e) => setFormData((p) => ({ ...p, ambito: String(e.target.value || '') }))}
                >
                  <MenuItem value="cliente">Cliente</MenuItem>
                  <MenuItem value="studio">Studio</MenuItem>
                  <MenuItem value="entrambi">Entrambi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Codice"
                size="small"
                fullWidth
                value={formData.codice}
                onChange={(e) => setFormData((p) => ({ ...p, codice: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrizione"
                size="small"
                fullWidth
                value={formData.descrizione}
                onChange={(e) => setFormData((p) => ({ ...p, descrizione: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Ordine"
                size="small"
                fullWidth
                type="number"
                value={formData.ordine}
                onChange={(e) => setFormData((p) => ({ ...p, ordine: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={Boolean(formData.attivo)}
                  onChange={(e) => setFormData((p) => ({ ...p, attivo: e.target.checked }))}
                  size="small"
                />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Attivo
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Elimina motivazione</DialogTitle>
        <DialogContent>
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <Typography className="font-semibold">
                  {deleteTarget?.codice || (deleteTarget?.id_motivazione ? `#${deleteTarget.id_motivazione}` : 'n/d')}
                </Typography>
                <Typography variant="body2" color="textSecondary" className="truncate">
                  {deleteTarget?.descrizione || ''}
                </Typography>
              </div>
            </div>
            <Typography variant="body2" color="textSecondary">
              Questa operazione non è reversibile.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
